import FileUpload from '@/shared/components/custom/file-upload';
import { UploadedFile } from '@/shared/types/file-upload';
import { Loader2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFrameworkWizardStore } from './states';

const Badge = ({
  children,
  onRemove,
}: {
  children: string;
  onRemove?: () => void;
}) => (
  <div className="box-border flex flex-row items-center py-0.5 px-1.5 gap-0.5 h-4 bg-[rgba(122,120,142,0.1)] border border-[#7A788E] rounded-[36px]">
    <span className="text-[10px] font-medium leading-3 text-center text-[#7A788E]">
      {children}
    </span>
    {onRemove && (
      <button onClick={onRemove} className="w-3 h-3 text-[#52525B]">
        <X size={12} />
      </button>
    )}
  </div>
);

export default function StepTwo() {
  const { stepTwoData, updateStepTwoData } = useFrameworkWizardStore();

  const selectedControls = [
    'Control 1',
    'Control 2',
    'Control 3',
    'Control 4',
    'Control 5',
    'Control 6',
    'Control 7',
  ];

  const [isDownloading, setIsDownloading] = useState(false);

  const handleFilesChange = (files: UploadedFile[], type: string) => {
    // If files array is empty (all files deleted), reset filledTemplateUrl
    if (files.length === 0) {
      updateStepTwoData({
        filledTemplateUrl: undefined,
        files: [],
      });
      return;
    }

    // Get the first successfully uploaded file's signed URL
    const successfulFile = files.find(
      (file) => file.status === 'success' && file.signedUrl
    );
    if (successfulFile?.signedUrl) {
      updateStepTwoData({
        filledTemplateUrl: successfulFile.signedUrl,
      });
    }

    // Keep existing functionality
    updateStepTwoData({ files });
  };

  const handleDownloadTemplate = () => {
    if (stepTwoData.templateUrl) {
      setIsDownloading(true);
      const link = document.createElement('a');
      link.href = stepTwoData.templateUrl;
      link.download = 'framework-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Fallback: reset loading after a short delay (since browser handles download)
      setTimeout(() => setIsDownloading(false), 1200);
    }
  };

  const [fileErrors, setFileErrors] = useState(false);

  // Helper to check for file errors
  const hasFileErrors = useMemo(() => {
    const files = stepTwoData.files || [];
    return files.some(
      (file) =>
        file.status === 'too_large' ||
        file.status === 'too_many_files' ||
        file.status === 'duplicate_files' ||
        file.status === 'failed'
    );
  }, [stepTwoData.files]);

  return (
    <div className="flex flex-col items-start gap-4 w-full h-full self-stretch flex-grow">
      <div className="flex flex-row justify-between items-center w-full">
        <p className="text-sm font-semibold text-[#18181B]">
          Import your framework controls into the Agent.
        </p>
      </div>

      <div className="box-border flex flex-row justify-center items-start w-full flex-grow bg-white border border-[#E4E4E7] rounded-lg overflow-hidden relative">
        {/* Main Content Panel */}
        <div className="flex flex-col items-center p-6 gap-6 h-full bg-white w-full max-w-2xl mx-auto">
          {/* Step 1: Download Template */}
          <div className="flex flex-col items-start gap-3 w-full max-w-lg">
            <div className="flex flex-col items-start gap-1.5 w-full">
              <h3 className="text-sm font-medium text-[#11181C] w-full">
                Step 1: Download Template
              </h3>
              <p className="text-sm text-[#71717A] w-full">
                Use this Excel file to ensure your control data is formatted
                correctly.
              </p>
            </div>
            <button
              className="box-border flex flex-row justify-center items-center py-1 px-3 gap-1.5 h-7 bg-white border border-[#006FEE] rounded-md whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleDownloadTemplate}
              disabled={!stepTwoData.templateUrl || isDownloading}
            >
              {isDownloading && (
                <Loader2 size={16} className="animate-spin mr-2" />
              )}
              <span className="text-sm font-medium text-[#006FEE]">
                Download Template
              </span>
            </button>
          </div>

          {/* Step 2: Fill in Details */}
          <div className="flex flex-col items-start gap-1.5 w-full max-w-lg">
            <h3 className="text-sm font-medium text-[#11181C] w-full">
              Step 2: Fill in Details
            </h3>
            <p className="text-sm text-[#71717A] w-full">
              Add your control data into the downloaded file.
            </p>
          </div>

          {/* Step 3: Upload Completed File */}
          <div className="flex flex-col items-start gap-1.5 w-full max-w-lg">
            <h3 className="text-sm font-medium text-[#11181C] w-full">
              Step 3: Upload Completed File
            </h3>
            <div className="flex flex-col items-start gap-4 w-full">
              <div className="w-full">
                <FileUpload
                  initialFiles={stepTwoData.files || []}
                  isMultiple={false}
                  maxFiles={1}
                  allowedFileTypes={[
                    'text/csv',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  ]}
                  setFiles={handleFilesChange}
                  type="framework-controls"
                  orientation="vertical"
                  maxSizeMB={20}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Pass hasFileErrors to HeaderSection if needed in parent */}
    </div>
  );
}
